import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { Menu, X, Globe, ChevronDown, LayoutDashboard, LogOut, User, Settings, Package } from 'lucide-react';
import GoldenSpiral from '@/components/effects/GoldenSpiral';

const LOGO_URL = '/assets/logo.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close the user dropdown on outside click or Escape
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e) => {
      if (!e.target.closest('[data-user-menu-root]')) setUserMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/book', label: t('nav.book') },
    { path: '/courses', label: t('nav.courses') },
    { path: '/events', label: t('nav.events') },
    { path: '/journal', label: t('nav.journal') },
    { path: '/forum', label: t('nav.forum') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const trackLabel = lang === 'ar' ? 'تتبع الطلب' : 'Track Order';

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50" style={{
        background: 'rgba(255, 248, 225, 0.8)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(139, 105, 20, 0.08)',
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
              <img src={LOGO_URL} alt="TariqWithin" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`text-sm font-body tracking-wide transition-colors duration-300 ${
                    isActive(link.path) ? 'text-sacred-gold' : 'text-warm-brown hover:text-heading'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/track-order"
                data-testid="nav-track-order"
                className={`hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-full glass-button text-xs whitespace-nowrap transition-colors ${isActive('/track-order') ? 'text-sacred-gold' : 'text-warm-brown hover:text-heading'}`}
              >
                <Package size={13} />
                <span>{trackLabel}</span>
              </Link>
              <button
                onClick={toggleLang}
                data-testid="lang-toggle"
                className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full glass-button text-xs whitespace-nowrap"
              >
                <Globe size={14} />
                <span>{lang === 'en' ? '\u0627\u0644\u0639\u0631\u0628\u064a\u0629' : 'English'}</span>
              </button>

              {user ? (
                <div className="relative" data-user-menu-root>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu-btn"
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    className="inline-flex items-center justify-center gap-2 h-8 px-3 rounded-full glass-button text-xs whitespace-nowrap"
                  >
                    <span className="text-deep-brown">{user.display_name || user.username}</span>
                    <ChevronDown size={12} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-48 glass-card rounded-xl py-2 z-50" data-testid="user-dropdown">
                      <Link to={`/profile/${user.username}`} className="flex items-center gap-2 px-4 py-2 text-sm text-warm-brown hover:text-heading hover:bg-sacred-gold/10" onClick={() => setUserMenuOpen(false)}>
                        <User size={14} /> {t('nav.profile')}
                      </Link>
                      <Link to="/settings" className="flex items-center gap-2 px-4 py-2 text-sm text-warm-brown hover:text-heading hover:bg-sacred-gold/10" onClick={() => setUserMenuOpen(false)}>
                        <Settings size={14} /> {t('nav.settings')}
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm text-sacred-gold hover:bg-sacred-gold/10" onClick={() => setUserMenuOpen(false)}>
                          <LayoutDashboard size={14} /> {t('nav.admin')}
                        </Link>
                      )}
                      <button onClick={() => { logout(); setUserMenuOpen(false); }} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-text-muted hover:text-heading hover:bg-sacred-gold/10" data-testid="logout-btn">
                        <LogOut size={14} /> {t('nav.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/auth" data-testid="nav-signin" className="inline-flex items-center justify-center h-8 px-4 rounded-full gold-button text-xs font-semibold whitespace-nowrap">
                  {t('nav.login')}
                </Link>
              )}

              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden text-heading p-2"
                data-testid="mobile-menu-toggle"
                aria-label="Open menu"
                aria-expanded={mobileOpen}
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col" data-testid="mobile-menu" style={{
          background: 'rgba(255, 243, 206, 0.95)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
        }}>
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none flex items-center justify-center">
            <GoldenSpiral size={400} />
          </div>
          <div className="flex justify-end p-4">
            <button onClick={() => setMobileOpen(false)} className="text-heading p-2" data-testid="mobile-menu-close">
              <X size={28} />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center flex-1 gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`text-2xl font-display tracking-wide ${isActive(link.path) ? 'text-sacred-gold' : 'text-heading/80'}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/track-order"
              onClick={() => setMobileOpen(false)}
              className={`text-xl font-body inline-flex items-center gap-2 ${isActive('/track-order') ? 'text-sacred-gold' : 'text-warm-brown'}`}
              data-testid="mobile-nav-track-order"
            >
              <Package size={16} /> {trackLabel}
            </Link>
            {user ? (
              <>
                <Link to={`/profile/${user.username}`} onClick={() => setMobileOpen(false)} className="text-xl text-warm-brown">{t('nav.profile')}</Link>
                <Link to="/settings" onClick={() => setMobileOpen(false)} className="text-xl text-warm-brown">{t('nav.settings')}</Link>
                {user.role === 'admin' && <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-xl text-sacred-gold">{t('nav.admin')}</Link>}
              </>
            ) : (
              <Link to="/auth" onClick={() => setMobileOpen(false)} className="px-8 py-3 rounded-full gold-button text-lg">{t('nav.login')}</Link>
            )}
          </div>
        </div>
      )}

      <div className="h-16 md:h-20" />
    </>
  );
}
